import { EnumUtils } from '../../utils/enum-utils';

type FontWeight = '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | '1000';

enum FontWeightName {
  Thin_100 = 'Thin_100',
  ExtraLight_200 = 'ExtraLight_200',
  Light_300 = 'Light_300',
  Regular_400 = 'Regular_400',
  Medium_500 = 'Medium_500',
  SemiBold_600 = 'SemiBold_600',
  Bold_700 = 'Bold_700',
  ExtraBold_800 = 'ExtraBold_800',
  Black_900 = 'Black_900',
  BlackExtra_1000 = 'BlackExtra_1000'
}

/**
 * Value needs to match the font family name provided in Adobe File or font-family.scss
 */
enum FontFamilyName {
  AcuminProExtraCondensed = 'acumin-pro-extra-condensed',
  AcuminProWide = 'acumin-pro-wide',
  AlternateGothicNo1d = 'alternate-gothic-no-1-d',
  AlternateGothicNo2d = 'alternate-gothic-no-2-d',
  AlternateGothicNo3d = 'alternate-gothic-no-3-d',
  Anton = 'anton',
  Apparat = 'apparat',
  Archivo = 'Archivo',
  ArchivoBlack = 'archivo-black',
  Arial = 'Arial',
  BanksMiles = 'Banks-Miles',
  BebasNeuePro = 'bebas-neue-pro',
  BebasNeueProExpanded = 'bebas-neue-pro-expanded',
  BigNoodleTitling = 'Big-Noodle-Titling',
  Calluna = 'calluna',
  Courier = 'courier-std',
  Dongle = 'Dongle',
  EdsMarketNarrowSlant = 'eds-market-narrow-slant',
  FacileSans = 'Facile-Sans',
  Fields = 'fields',
  FieldsDisplay = 'fields-display',
  FuturaPt = 'futura-pt',
  Gilroy = 'Gilroy',
  Heebo = 'heebo',
  Indivisible = 'indivisible',
  Inter = 'Inter',
  Karla = 'Karla',
  Lato = 'lato',
  MerriWeather = 'merriweather',
  Metropolis = 'Metropolis',
  MontExtraLight = 'Mont-ExtraLight',
  MontHeavy = 'Mont-Heavy',
  Montserrat = 'Montserrat',
  MyriadPro = 'myriad-pro',
  NeueHaasGroteskDisplay = 'NeueHaasGroteskDisplay',
  Nunito = 'nunito',
  NunitoSans = 'nunito-sans',
  Onest = 'Onest',
  OpenSans = 'open-sans',
  Oswald = 'oswald',
  P22MackinacPro = 'P22-Mackinac-Pro',
  PlayfairDisplay = 'playfair-display',
  Poppins = 'poppins',
  Pragmatica = 'pragmatica',
  PragmaticaExtended = 'pragmatica-extended',
  ProximaNova = 'proxima-nova',
  PtMono = 'pt-mono',
  Raleway = 'raleway',
  RamaGothicM = 'RamaGothicM',
  RealistNarrow = 'realistnarrow',
  RealistWide = 'realistwide',
  RiftSoft = 'rift-soft',
  Roboto = 'Roboto',
  Rubik = 'rubik',
  Shelby = 'shelby',
  Soleil = 'Soleil',
  SourceSansPro = 'source-sans-pro',
  Spectral = 'Spectral',
  SuisseIntl = 'SuisseIntl',
  Tzimmes = 'tzimmes',
  UbuntuTitled = 'Ubuntu-Titled',
  WindsorBold = 'Windsor-Bold',
  WorkSans = 'Work-Sans'
}

export type FontStyleSheet = { fontFamily: FontFamilyName; fontWeight?: FontWeight };
type FontFamilyKeyName = keyof typeof FontFamilyName;
const FontFamilyKey = EnumUtils.createEnum<FontFamilyKeyName>(Object.keys(FontFamilyName) as FontFamilyKeyName[]);
const createFont = (fontFamily: FontFamilyName, fontWeight: FontWeight | undefined): FontStyleSheet => {
  return { fontFamily, fontWeight };
};

export const FONT = {
  [FontFamilyKey.AcuminProExtraCondensed]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.AcuminProExtraCondensed, '400')
  },
  [FontFamilyKey.AcuminProWide]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.AcuminProWide, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.AcuminProWide, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.AcuminProWide, '700')
  },
  [FontFamilyKey.AlternateGothicNo1d]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.AlternateGothicNo1d, '400')
  },
  [FontFamilyKey.AlternateGothicNo2d]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.AlternateGothicNo2d, '400')
  },
  [FontFamilyKey.AlternateGothicNo3d]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.AlternateGothicNo3d, '400')
  },
  [FontFamilyKey.Anton]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Anton, '400')
  },
  [FontFamilyKey.Apparat]: {
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Apparat, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Apparat, '500'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Apparat, '600'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Apparat, '700')
  },
  [FontFamilyKey.Archivo]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Archivo, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Archivo, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Archivo, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Archivo, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Archivo, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Archivo, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Archivo, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Archivo, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Archivo, '900')
  },
  [FontFamilyKey.ArchivoBlack]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.ArchivoBlack, '400')
  },
  [FontFamilyKey.Arial]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Arial, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Arial, '600'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Arial, '800')
  },
  [FontFamilyKey.BanksMiles]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.BanksMiles, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.BanksMiles, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.BanksMiles, '700')
  },
  [FontFamilyKey.BebasNeuePro]: {
    [FontWeightName.Light_300]: createFont(FontFamilyName.BebasNeuePro, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.BebasNeuePro, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.BebasNeuePro, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.BebasNeuePro, '600')
  },
  [FontFamilyKey.BebasNeueProExpanded]: {
    [FontWeightName.Bold_700]: createFont(FontFamilyName.BebasNeueProExpanded, '700')
  },
  [FontFamilyKey.BigNoodleTitling]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.BigNoodleTitling, undefined)
  },
  [FontFamilyKey.Calluna]: {
    [FontWeightName.Light_300]: createFont(FontFamilyName.Calluna, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Calluna, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Calluna, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Calluna, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Calluna, '900')
  },
  [FontFamilyKey.Courier]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Courier, '400'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Courier, '700')
  },
  [FontFamilyKey.Dongle]: {
    [FontWeightName.Light_300]: createFont(FontFamilyName.Dongle, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Dongle, '400'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Dongle, '700')
  },
  [FontFamilyKey.EdsMarketNarrowSlant]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.EdsMarketNarrowSlant, '400')
  },
  [FontFamilyKey.FacileSans]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.FacileSans, '400')
  },
  [FontFamilyKey.Fields]: {
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Fields, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Fields, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Fields, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Fields, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Fields, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Fields, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Fields, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Fields, '900')
  },
  [FontFamilyKey.FieldsDisplay]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.FieldsDisplay, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.FieldsDisplay, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.FieldsDisplay, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.FieldsDisplay, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.FieldsDisplay, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.FieldsDisplay, '900')
  },
  [FontFamilyKey.FuturaPt]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.FuturaPt, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.FuturaPt, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.FuturaPt, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.FuturaPt, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.FuturaPt, '800')
  },
  [FontFamilyKey.Gilroy]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Gilroy, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Gilroy, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Gilroy, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Gilroy, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Gilroy, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Gilroy, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Gilroy, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Gilroy, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Gilroy, '900')
  },
  [FontFamilyKey.Heebo]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Heebo, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Heebo, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Heebo, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Heebo, '500'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Heebo, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Heebo, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Heebo, '900')
  },
  [FontFamilyKey.Indivisible]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Indivisible, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Indivisible, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Indivisible, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Indivisible, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Indivisible, '900')
  },
  [FontFamilyKey.Inter]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Inter, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Inter, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Inter, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Inter, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Inter, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Inter, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Inter, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Inter, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Inter, '900')
  },
  [FontFamilyKey.Karla]: {
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Karla, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Karla, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Karla, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Karla, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Karla, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Karla, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Karla, '800')
  },
  [FontFamilyKey.Lato]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Lato, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Lato, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Lato, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Lato, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Lato, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Lato, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Lato, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Lato, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Lato, '900')
  },
  [FontFamilyKey.MerriWeather]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.MerriWeather, '400'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.MerriWeather, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.MerriWeather, '900')
  },
  [FontFamilyKey.Metropolis]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Metropolis, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Metropolis, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Metropolis, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Metropolis, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Metropolis, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Metropolis, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Metropolis, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Metropolis, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Metropolis, '900')
  },
  [FontFamilyKey.MontExtraLight]: {
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.MontExtraLight, '200')
  },
  [FontFamilyKey.MontHeavy]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.MontHeavy, undefined)
  },
  [FontFamilyKey.Montserrat]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Montserrat, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Montserrat, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Montserrat, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Montserrat, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Montserrat, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Montserrat, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Montserrat, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Montserrat, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Montserrat, '900')
  },
  [FontFamilyKey.MyriadPro]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.MyriadPro, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.MyriadPro, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.MyriadPro, '700')
  },
  [FontFamilyKey.NeueHaasGroteskDisplay]: {
    [FontWeightName.Light_300]: createFont(FontFamilyName.NeueHaasGroteskDisplay, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.NeueHaasGroteskDisplay, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.NeueHaasGroteskDisplay, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.NeueHaasGroteskDisplay, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.NeueHaasGroteskDisplay, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.NeueHaasGroteskDisplay, '900')
  },
  [FontFamilyKey.Nunito]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Nunito, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Nunito, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Nunito, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Nunito, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Nunito, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Nunito, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Nunito, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Nunito, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Nunito, '900')
  },
  [FontFamilyKey.NunitoSans]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.NunitoSans, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.NunitoSans, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.NunitoSans, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.NunitoSans, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.NunitoSans, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.NunitoSans, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.NunitoSans, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.NunitoSans, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.NunitoSans, '900')
  },
  [FontFamilyKey.Onest]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Onest, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Onest, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Onest, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Onest, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Onest, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Onest, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Onest, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Onest, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Onest, '900')
  },
  [FontFamilyKey.OpenSans]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.OpenSans, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.OpenSans, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.OpenSans, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.OpenSans, '800')
  },
  [FontFamilyKey.Oswald]: {
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Oswald, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Oswald, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Oswald, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Oswald, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Oswald, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Oswald, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Oswald, '900')
  },
  [FontFamilyKey.P22MackinacPro]: {
    [FontWeightName.Bold_700]: createFont(FontFamilyName.P22MackinacPro, '700')
  },
  [FontFamilyKey.PlayfairDisplay]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.PlayfairDisplay, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.PlayfairDisplay, '500'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.PlayfairDisplay, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.PlayfairDisplay, '900')
  },
  [FontFamilyKey.Poppins]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Poppins, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Poppins, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Poppins, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Poppins, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Poppins, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Poppins, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Poppins, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Poppins, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Poppins, '900')
  },
  [FontFamilyKey.Pragmatica]: {
    [FontWeightName.Light_300]: createFont(FontFamilyName.Pragmatica, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Pragmatica, '400')
  },
  [FontFamilyKey.PragmaticaExtended]: {
    [FontWeightName.Medium_500]: createFont(FontFamilyName.PragmaticaExtended, '500')
  },
  [FontFamilyKey.ProximaNova]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.ProximaNova, '100'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.ProximaNova, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.ProximaNova, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.ProximaNova, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.ProximaNova, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.ProximaNova, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.ProximaNova, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.ProximaNova, '900')
  },
  [FontFamilyKey.PtMono]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.PtMono, '400'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.PtMono, '700')
  },
  [FontFamilyKey.Raleway]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Raleway, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Raleway, '500'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Raleway, '700')
  },
  [FontFamilyKey.RamaGothicM]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.RamaGothicM, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.RamaGothicM, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.RamaGothicM, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.RamaGothicM, '900')
  },
  [FontFamilyKey.RealistNarrow]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.RealistNarrow, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.RealistNarrow, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.RealistNarrow, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.RealistNarrow, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.RealistNarrow, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.RealistNarrow, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.RealistNarrow, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.RealistNarrow, '900')
  },
  [FontFamilyKey.RealistWide]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.RealistWide, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.RealistWide, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.RealistWide, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.RealistWide, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.RealistWide, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.RealistWide, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.RealistWide, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.RealistWide, '900')
  },
  [FontFamilyKey.RiftSoft]: {
    [FontWeightName.Bold_700]: createFont(FontFamilyName.RiftSoft, '700')
  },
  [FontFamilyKey.Roboto]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Roboto, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Roboto, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Roboto, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Roboto, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Roboto, '900'),
    [FontWeightName.BlackExtra_1000]: createFont(FontFamilyName.Roboto, '1000')
  },
  [FontFamilyKey.Rubik]: {
    [FontWeightName.Light_300]: createFont(FontFamilyName.Rubik, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Rubik, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Rubik, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Rubik, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Rubik, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Rubik, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Rubik, '900')
  },
  [FontFamilyKey.Shelby]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Shelby, '400'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Shelby, '700')
  },
  [FontFamilyKey.Soleil]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Soleil, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Soleil, '500'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Soleil, '600'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Soleil, '700')
  },
  [FontFamilyKey.SourceSansPro]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.SourceSansPro, '400'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.SourceSansPro, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.SourceSansPro, '700'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.SourceSansPro, '900')
  },
  [FontFamilyKey.Spectral]: {
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Spectral, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Spectral, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Spectral, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Spectral, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Spectral, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Spectral, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Spectral, '800')
  },
  [FontFamilyKey.SuisseIntl]: {
    [FontWeightName.Regular_400]: createFont(FontFamilyName.SuisseIntl, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.SuisseIntl, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.SuisseIntl, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.SuisseIntl, '700')
  },
  [FontFamilyKey.Tzimmes]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.Tzimmes, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.Tzimmes, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.Tzimmes, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.Tzimmes, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.Tzimmes, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.Tzimmes, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.Tzimmes, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.Tzimmes, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.Tzimmes, '900')
  },
  [FontFamilyKey.UbuntuTitled]: {
    [FontWeightName.Bold_700]: createFont(FontFamilyName.UbuntuTitled, undefined)
  },
  [FontFamilyKey.WindsorBold]: {
    [FontWeightName.Bold_700]: createFont(FontFamilyName.WindsorBold, '700')
  },
  [FontFamilyKey.WorkSans]: {
    [FontWeightName.Thin_100]: createFont(FontFamilyName.WorkSans, '100'),
    [FontWeightName.ExtraLight_200]: createFont(FontFamilyName.WorkSans, '200'),
    [FontWeightName.Light_300]: createFont(FontFamilyName.WorkSans, '300'),
    [FontWeightName.Regular_400]: createFont(FontFamilyName.WorkSans, '400'),
    [FontWeightName.Medium_500]: createFont(FontFamilyName.WorkSans, '500'),
    [FontWeightName.SemiBold_600]: createFont(FontFamilyName.WorkSans, '600'),
    [FontWeightName.Bold_700]: createFont(FontFamilyName.WorkSans, '700'),
    [FontWeightName.ExtraBold_800]: createFont(FontFamilyName.WorkSans, '800'),
    [FontWeightName.Black_900]: createFont(FontFamilyName.WorkSans, '900')
  }
} as const;
